import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Optional, Union } from "@/__main__/data-model.mjs";
import { paymentProviderTypeModel } from "@/feature-wallet/models/payment-method-provider.mjs";

const subscriptionStatus = Union([
  "incomplete",
  "incomplete_expired",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
]);

export const userSubscriptionModel = {
  id: String,
  status: subscriptionStatus,
  planName: String,
  planUnitAmount: Number,
  providerType: Optional(paymentProviderTypeModel),
  paymentMethodId: Optional(String),
  willCancelAtPeriodEnd: Boolean,
  createdAt: Date,
  currentPeriodEnd: Optional(Date),
  trialEnd: Optional(Date),
  coupon: Optional({
    duration: String,
    percentOff: Optional(Number),
    amountOff: Optional(Number),
    endAt: Date,
  }),
} satisfies ModelDefinition;

export interface UserSubscription
  extends FromModel<typeof userSubscriptionModel> {}

export const UserSubscriptionsModel = createModel({
  data: { userPaymentInfo: { paymentSubscriptions: [userSubscriptionModel] } },
}).transform([userSubscriptionModel], (data): UserSubscription[] => {
  return data.data.userPaymentInfo.paymentSubscriptions.map(
    normalizeUserSubscription,
  );
});

export function normalizeUserSubscription(
  subscription: UserSubscription,
): UserSubscription {
  if (subscription.paymentMethodId?.startsWith("pm_")) {
    return applyStripeTransformation(subscription);
  }

  return applyPaypalTransformation(subscription);
}

function applyStripeTransformation(subscription: UserSubscription) {
  // Stripe doesn't need any changes, but we have a dummy function here for consistency
  return subscription;
}

function applyPaypalTransformation(subscription: UserSubscription) {
  if (subscription.currentPeriodEnd) {
    // PayPal does this stupid thing where it stores the end date in your local time zone, but does not include the
    // timezone offset in the date string. Add the current timezone offset to the date to get it back to the correct
    // date which results in it being, annoyingly enough, your local timezone.
    subscription.currentPeriodEnd.setMinutes(
      subscription.currentPeriodEnd.getMinutes() +
        subscription.createdAt.getTimezoneOffset(),
    );

    // PayPal's end date appears to be inclusive (i.e. a sub starting on the 3rd would show an end date of the 2nd).
    subscription.currentPeriodEnd.setDate(
      subscription.currentPeriodEnd.getDate() + 1,
    );

    // PayPal sets the `status` to `canceled` immediately after the user cancels their subscription. This differs
    // from Stripe, so we'll transform the data to be consistent between the different providers. If Backend ever
    // does this transformation for us, this can be removed.
    if (
      subscription.status === "canceled" &&
      subscription.currentPeriodEnd > new Date()
    ) {
      subscription.status = "active";
      subscription.willCancelAtPeriodEnd = true;
    }
  }

  if (subscription.trialEnd) {
    subscription.trialEnd.setMinutes(
      subscription.trialEnd.getMinutes() +
        subscription.createdAt.getTimezoneOffset(),
    );
  }

  return subscription;
}
